function eventType(event) {
  return String(event?.action || event?.event_type || "").toUpperCase();
}

/** 把同一天的所有事实（交易、录入持仓、分红、送转配股……）分组。
 *
 * 不区分事件种类——同一天不管撞上哪几种，图上都只能画一个点，靠 `markOf`
 * 分类出的字（买/卖/持/分/配/送……）判断这组要不要合成一个标记：
 * 只有一种字（哪怕好几条，比如一天卖两次）用那个字；两种以上字的调用方
 * 负责拼接展示（2026-09-17 用户发现：先是录入持仓和当天卖出撞在一起被
 * 「actions[0]」吞掉，改成分开画又变成两个圆叠在同一个像素上互相盖住——
 * 按种类而不是按"是不是交易"分组，才不用为每一种新组合单独写分支）。
 *
 * 只影响展示：原始行原样挂在 `events` 里供 tooltip 逐条列出，核算数据不受影响。 */
export function mergeSameDayEvents(events = [], markOf) {
  const groups = new Map();

  for (const event of events ?? []) {
    const date = event?.trade_date ?? event?.date;
    if (!date) continue;
    const group = groups.get(date) ?? [];
    group.push({ ...event, date });
    groups.set(date, group);
  }

  return [...groups.entries()].map(([date, group]) => ({
    date,
    events: group,
    marks: [...new Set(group.map(markOf))],
  }));
}

/** Keep chart events inside the selected time window before mapping them to
 * the next available market-data date. This prevents an older event from
 * being visually moved to the left edge of a shortened chart range. */
export function performanceEventsWithinRange(events = [], { startDate, endDate } = {}) {
  return (events ?? []).filter((event) => {
    const date = event?.date;
    if (!date) return false;
    return (!startDate || date >= startDate) && (!endDate || date <= endDate);
  });
}

function dividendEvidenceKey(event) {
  if (event?.evidence_ref) return event.evidence_ref;
  const entitlementDate = event?.entitlement_date ?? event?.record_date ?? event?.ex_date ?? event?.date;
  return entitlementDate && event?.asset_code
    ? `dividend:${entitlementDate}:${event.asset_code}`
    : null;
}

// 标在股权登记日，和核算判定「谁有份」用的是同一个日期（single_asset_ledger.py
// 的 entitlement_date = record_date ?? ex_date ?? pay_date）。公告日不行：2024 年
// 以来 14203 条已实施分红，公告日到除权日平均隔 54.9 天，标在公告日会让买入在
// 公告之后的人以为「分红不在我的持有期内」（用户 2026-09-16 实例：安宁股份
// 05-23 公告、07-14 登记，两笔买入都在中间）。派息日也不用单独标：14206 条里
// 14205 条的除权日和派息日是同一天。
function dividendMarkerDate(event) {
  return event?.entitlement_date
    ?? event?.record_date
    ?? event?.ex_date
    ?? event?.announcement_date
    ?? event?.ann_date
    ?? event?.imp_ann_date
    ?? event?.date;
}

function dividendMarkerPriority(event) {
  const type = eventType(event);
  // 登记日那条当代表：图上标的是「谁有份」的那一天，不是公告那一天。
  if (type === "CASH_DIVIDEND_ENTITLEMENT") return 0;
  if (type === "CASH_DIVIDEND") return 1;
  if (type === "CASH_DIVIDEND_ANNOUNCEMENT") return 2;
  return 3;
}

/** Collapse internal announcement/entitlement/payment rows into one chart fact.
 * Financial recognition remains exclusively in the deterministic backend. */
export function visiblePerformanceAssetEvents(assetEvents = []) {
  const ordinaryEvents = [];
  const dividendGroups = new Map();

  for (const event of assetEvents ?? []) {
    if (!eventType(event).startsWith("CASH_DIVIDEND")) {
      ordinaryEvents.push(event);
      continue;
    }
    const key = dividendEvidenceKey(event) ?? `unkeyed:${dividendGroups.size}`;
    const group = dividendGroups.get(key) ?? [];
    group.push(event);
    dividendGroups.set(key, group);
  }

  const dividends = [...dividendGroups.values()].map((group) => {
    const representative = [...group].sort((left, right) => (
      dividendMarkerPriority(left) - dividendMarkerPriority(right)
    ))[0];
    // 整组里只要有一条给得出登记日就用它，不依赖代表那条带齐了字段。
    const entitlement = group
      .map((event) => event?.entitlement_date ?? event?.record_date ?? event?.ex_date)
      .find(Boolean);
    return {
      ...representative,
      date: entitlement ?? dividendMarkerDate(representative),
      chart_event_type: "cash_dividend_entitlement",
    };
  });

  return [...ordinaryEvents, ...dividends];
}
